/**
 * relationshipStateResolver.js
 * Pure function — maps entity context + visit data → structured relationship state.
 *
 * Inputs:
 *   entityContext  — { id, type, name, tone } from buildFamilyContext / buildPersonContext
 *   visitRows      — array from get_visit_timeline tool (may be empty or null)
 *
 * The entity tone is the primary signal when real visit data is unavailable:
 *   "followup_urgent" → >90 days no visit
 *   "followup_needed" → 45–90 days no visit
 *   "sensitive"       → urgent prayer(s) active
 *   "accompany"       → person in baptism process
 *   "neutral"         → stable situation
 */

import { getDaysSince } from "../date";

// ── Visit frequency thresholds (days) ────────────────────────────────────────
const FREQ_REGULAR  = 30;
const FREQ_SPORADIC = 90;

function deriveVisitFrequency(sortedVisits) {
  if (!sortedVisits?.length) return "absent";
  const lastDays = getDaysSince(sortedVisits[0].date);
  if (lastDays === null) return "absent";
  if (lastDays <= FREQ_REGULAR)  return "regular";
  if (lastDays <= FREQ_SPORADIC) return "sporadic";
  return "absent";
}

function deriveVisitCadence(sortedVisits) {
  if (!sortedVisits || sortedVisits.length < 2) return null;
  // Average gap between last 3 visits (days)
  const gaps = [];
  const recent = sortedVisits.slice(0, 3);
  for (let i = 0; i < recent.length - 1; i++) {
    const d0 = getDaysSince(recent[i].date);
    const d1 = getDaysSince(recent[i + 1].date);
    if (d0 !== null && d1 !== null) gaps.push(d0 - d1);
  }
  if (gaps.length === 0) return null;
  return Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length);
}

function deriveAttendanceSignal(sortedVisits, previousSignal) {
  if (!sortedVisits?.length) return "absent";
  const freq = deriveVisitFrequency(sortedVisits);
  if (freq === "absent") return "absent";

  // Compare with previous tone if we have history
  if (previousSignal) {
    if (previousSignal === "followup_urgent" && freq !== "absent") return "returning";
    if (previousSignal === "neutral" && freq === "absent")          return "declining";
    if (previousSignal === "followup_needed" && freq === "absent")  return "declining";
  }

  return freq === "regular" ? "consistent" : "sporadic";
}

// ── Tone-based fallback ───────────────────────────────────────────────────────
const TONE_STATE = {
  followup_urgent: { followup_frequency: "absent",   last_contact_days: 90, pastoral_risk: "high"   },
  followup_needed: { followup_frequency: "sporadic",  last_contact_days: 50, pastoral_risk: "medium" },
  sensitive:       { followup_frequency: "sporadic",  last_contact_days: null, pastoral_risk: "high" },
  accompany:       { followup_frequency: "regular",  last_contact_days: null, pastoral_risk: "low"   },
  neutral:         { followup_frequency: "regular",  last_contact_days: null, pastoral_risk: "none"  },
};

function compositeRisk(lastDays, prayerCount, tone) {
  // Prayer count + visit absence → composite risk
  if (prayerCount >= 3 && (lastDays === null || lastDays > 30)) return "high";
  if (lastDays !== null && lastDays > 90)  return "high";
  if (lastDays !== null && lastDays > 45)  return "medium";
  if (prayerCount >= 2)                    return "medium";
  if (tone === "sensitive")                return "high";
  if (tone === "followup_needed")          return "medium";
  return "low";
}

/**
 * @param {{
 *   entityContext: { id: string, type: string, name: string, tone: string },
 *   visitRows:     Object[] | null,
 *   prevSignalTone: string | null,
 * }} params
 * @returns {{
 *   followup_frequency: "regular"|"sporadic"|"absent",
 *   last_contact_days:  number|null,
 *   pastoral_risk:      "none"|"low"|"medium"|"high",
 *   visit_count_recent: number,
 *   attendance_signal:  "consistent"|"sporadic"|"declining"|"returning"|"absent",
 *   avg_visit_gap_days: number|null,
 *   source:             "live"|"tone",
 * }}
 */
export function resolveRelationshipState({ entityContext, visitRows, prevSignalTone = null }) {
  const tone = entityContext?.tone ?? "neutral";

  // ── Live data path ────────────────────────────────────────────────────────
  if (visitRows?.length > 0) {
    const sorted = [...visitRows].sort((a, b) => new Date(b.date) - new Date(a.date));
    const lastDays = getDaysSince(sorted[0].date);

    // Recent visits = last 60 days
    const recent60 = sorted.filter((v) => {
      const d = getDaysSince(v.date);
      return d !== null && d <= 60;
    });

    const followup_frequency  = deriveVisitFrequency(sorted);
    const attendance_signal   = deriveAttendanceSignal(sorted, prevSignalTone);
    const avg_visit_gap_days  = deriveVisitCadence(sorted);

    return {
      followup_frequency,
      last_contact_days:  lastDays,
      pastoral_risk:      compositeRisk(lastDays, 0, tone),
      visit_count_recent: recent60.length,
      attendance_signal,
      avg_visit_gap_days,
      source: "live",
    };
  }

  // ── Tone-based fallback ───────────────────────────────────────────────────
  const base = TONE_STATE[tone] ?? TONE_STATE.neutral;
  return {
    ...base,
    visit_count_recent: 0,
    attendance_signal: base.followup_frequency === "absent" ? "absent" : "sporadic",
    avg_visit_gap_days: null,
    source: "tone",
  };
}
